import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogPortal,
  DialogOverlay,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
} from '@/components/ui/alert-dialog';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { CalendarIcon, Repeat, Trash2, X } from 'lucide-react';
import { format, addMonths, getDate, getDay } from 'date-fns';
import { cn } from '@/lib/utils';
import { RecurrencePattern, RecurrenceEndType } from '@/types';

interface RecurrenceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  recurrencePattern: RecurrencePattern;
  onRecurrencePatternChange: (pattern: RecurrencePattern) => void;
  customInterval: string;
  onCustomIntervalChange: (interval: string) => void;
  recurrenceEndType: RecurrenceEndType;
  onRecurrenceEndTypeChange: (type: RecurrenceEndType) => void;
  occurrences: string;
  onOccurrencesChange: (occurrences: string) => void;
  recurrenceEndDate: Date | undefined;
  onRecurrenceEndDateChange: (date: Date | undefined) => void;
  onDaysOfWeekChange?: (days: string[]) => void;
  onDayOfMonthChange?: (day: number | undefined) => void;
  onMonthlyRecurrenceTypeChange?: (type: 'day-of-month' | 'relative-weekday') => void;
  onRelativeWeekdayChange?: (value: { occurrence: 'first' | 'second' | 'third' | 'fourth' | 'last'; weekday: string } | undefined) => void;
  onSeriesStartDateChange?: (date: Date | undefined) => void;
  // Current values for initialization when editing
  daysOfWeek?: string[];
  monthlyRecurrenceType?: 'day-of-month' | 'relative-weekday';
  startDate: Date | undefined;
  bookingEndDate?: Date | undefined;
  startTime?: string;
  endTime?: string;
  durationType?: 'hourly' | 'half-day' | 'half-day-am' | 'half-day-pm' | 'full-day';
  errors?: {
    occurrences?: string;
    recurrenceEndDate?: string;
  };
  containerRef?: React.RefObject<HTMLDivElement | null>;
}

const DAYS_OF_WEEK = [
  { label: 'M', value: 'monday',    fullName: 'Monday'    },
  { label: 'T', value: 'tuesday',   fullName: 'Tuesday'   },
  { label: 'W', value: 'wednesday', fullName: 'Wednesday' },
  { label: 'T', value: 'thursday',  fullName: 'Thursday'  },
  { label: 'F', value: 'friday',    fullName: 'Friday'    },
  { label: 'S', value: 'saturday',  fullName: 'Saturday'  },
  { label: 'S', value: 'sunday',    fullName: 'Sunday'    },
];

type RepeatUnit = 'day' | 'week' | 'month';

type MonthlyType = 'day-of-month' | 'weekday-of-month';

const getWeekdayName = (date: Date): string => {
  const weekdays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  return weekdays[getDay(date)];
};

const getOrdinalLabel = (date: Date): string => {
  const dayOfMonth = getDate(date);
  const lastDay    = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  if (dayOfMonth + 7 > lastDay) return 'last';
  const week = Math.ceil(dayOfMonth / 7);
  return ['first', 'second', 'third', 'fourth'][week - 1] ?? 'first';
};

export function RecurrenceDialog({
  open,
  onOpenChange,
  recurrencePattern,
  onRecurrencePatternChange,
  customInterval,
  onCustomIntervalChange,
  recurrenceEndType,
  onRecurrenceEndTypeChange,
  occurrences,
  onOccurrencesChange,
  recurrenceEndDate,
  onRecurrenceEndDateChange,
  startDate,
  bookingEndDate,
  startTime,
  endTime,
  durationType,
  errors = {},
  containerRef,
  onDaysOfWeekChange,
  onDayOfMonthChange,
  onMonthlyRecurrenceTypeChange,
  onRelativeWeekdayChange,
  onSeriesStartDateChange,
  daysOfWeek: propDaysOfWeek,
  monthlyRecurrenceType: propMonthlyRecurrenceType,
}: RecurrenceDialogProps) {
  const [seriesStartDate, setSeriesStartDate]     = useState<Date | undefined>(startDate);
  const [seriesStartOpen, setSeriesStartOpen]     = useState(false);
  const [repeatUnit, setRepeatUnit]               = useState<RepeatUnit>(
    recurrencePattern === 'daily' ? 'day' : recurrencePattern === 'monthly' ? 'month' : 'week'
  );
  const [repeatInterval, setRepeatInterval]       = useState(customInterval || '1');
  const [showAdvanced, setShowAdvanced]           = useState(false);
  const [selectedDays, setSelectedDays]           = useState<string[]>([]);
  const [endDate, setEndDate]                     = useState<Date | undefined>(recurrenceEndDate);
  const [endDateOpen, setEndDateOpen]             = useState(false);
  const [monthlyType, setMonthlyType]             = useState<MonthlyType>('day-of-month');
  const [validationErrors, setValidationErrors]   = useState<{ endDate?: string }>({});
  const [durationOverlapAlert, setDurationOverlapAlert] = useState(false);
  const [containerPosition, setContainerPosition] = useState<{
    left: number; top: number; width: number; height: number;
  } | null>(null);

  // ── Multi-day booking check ──────────────────────────────────────────────────
  const isMultiDayBooking = (): boolean => {
    if (durationType === 'full-day' && startDate && bookingEndDate) {
      return Math.ceil((bookingEndDate.getTime() - startDate.getTime()) / 86400000) > 1;
    }
    if (durationType === 'half-day' && startDate && bookingEndDate) {
      return Math.ceil((bookingEndDate.getTime() - startDate.getTime()) / 86400000) >= 1;
    }
    if (durationType === 'hourly' && startDate && bookingEndDate && startTime && endTime) {
      const [sh, sm] = startTime.split(':').map(Number);
      const [eh, em] = endTime.split(':').map(Number);
      const s = new Date(startDate); s.setHours(sh, sm, 0, 0);
      const e = new Date(bookingEndDate); e.setHours(eh, em, 0, 0);
      return (e.getTime() - s.getTime()) / 3600000 > 24;
    }
    return false;
  };

  const isDailyDisabled = isMultiDayBooking();

  // ── Initialise when dialog opens ────────────────────────────────────────────
  useEffect(() => {
    if (open) {
      setSeriesStartDate(startDate);
      setEndDate(recurrenceEndDate ?? (startDate ? addMonths(startDate, 3) : undefined));
      const unit: RepeatUnit =
        recurrencePattern === 'daily' ? 'day' :
        recurrencePattern === 'monthly' ? 'month' : 'week';
      setRepeatUnit(unit);
      setRepeatInterval(customInterval || '1');
      setShowAdvanced(parseInt(customInterval || '1') > 1);
      // Initialize selectedDays from parent state (for editing) or from start date (for new)
      if (propDaysOfWeek && propDaysOfWeek.length > 0) {
        setSelectedDays(propDaysOfWeek);
      } else if (startDate) {
        const dayMap = ['sunday','monday','tuesday','wednesday','thursday','friday','saturday'];
        setSelectedDays([dayMap[startDate.getDay()]]);
      }
      // Initialize monthlyType from parent state (for editing) or default
      if (propMonthlyRecurrenceType === 'relative-weekday') {
        setMonthlyType('weekday-of-month');
      } else {
        setMonthlyType('day-of-month');
      }
      setValidationErrors({});
    }
  }, [open, startDate, recurrenceEndDate, recurrencePattern, customInterval]);

  useEffect(() => {
    if (isDailyDisabled && repeatUnit === 'day') setRepeatUnit('week');
  }, [isDailyDisabled, repeatUnit]);

  useEffect(() => {
    if (repeatUnit === 'day') setRepeatInterval('1');
  }, [repeatUnit]);

  // ── Container positioning ───────────────────────────────────────────────────
  useEffect(() => {
    const update = () => {
      if (containerRef?.current && open) {
        const r = containerRef.current.getBoundingClientRect();
        setContainerPosition({ left: r.left, top: r.top, width: r.width, height: r.height });
      }
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update);
    };
  }, [open, containerRef]);

  // ── Helpers ─────────────────────────────────────────────────────────────────
  const toggleDay = (day: string) => {
    setSelectedDays(prev =>
      prev.includes(day) ? (prev.length > 1 ? prev.filter(d => d !== day) : prev) : [...prev, day]
    );
  };

  // ── Live preview ─────────────────────────────────────────────────────────────
  const getPreviewText = (): string => {
    const interval = parseInt(repeatInterval) || 1;
    let pattern = '';

    if (repeatUnit === 'day') {
      pattern = interval === 1 ? 'Daily' : `Every ${interval} days`;

    } else if (repeatUnit === 'week') {
      const dayNames = selectedDays.length > 0
        ? selectedDays
            .sort((a, b) => DAYS_OF_WEEK.findIndex(d => d.value === a) - DAYS_OF_WEEK.findIndex(d => d.value === b))
            .map(d => DAYS_OF_WEEK.find(x => x.value === d)?.fullName.slice(0, 3))
            .join(', ')
        : seriesStartDate ? getWeekdayName(seriesStartDate).slice(0, 3) : '';
      pattern = interval === 1
        ? `Weekly on ${dayNames}`
        : `Every ${interval} weeks on ${dayNames}`;

    } else {
      const ref = seriesStartDate;
      if (monthlyType === 'day-of-month') {
        pattern = interval === 1
          ? `Monthly on day ${ref ? getDate(ref) : ''}`
          : `Every ${interval} months on day ${ref ? getDate(ref) : ''}`;
      } else {
        const weekday = ref ? getWeekdayName(ref) : 'Monday';
        const ord     = ref ? getOrdinalLabel(ref) : 'first';
        pattern = interval === 1
          ? `Monthly on the ${ord} ${weekday}`
          : `Every ${interval} months on the ${ord} ${weekday}`;
      }
    }

    const from  = seriesStartDate ? ` · from ${format(seriesStartDate, 'dd MMM yyyy')}` : '';
    const until = endDate ? ` · until ${format(endDate, 'dd MMM yyyy')}` : '';
    return `${pattern}${from}${until}`;
  };

  const getPositionStyles = (): React.CSSProperties => {
    if (!containerPosition) return {};
    return {
      left: `${containerPosition.left + containerPosition.width / 2}px`,
      top: `${containerPosition.top + containerPosition.height / 2}px`,
      transform: 'translate(-50%, -50%)',
    };
  };

  // ── Duration overlap check ─────────────────────────────────────────────────
  const DURATION_OVERLAP_MSG =
    'The duration of the booking must be shorter than how frequently it occurs. ' +
    'Shorten the duration, or change the recurrence pattern in the Booking repeat dialogue box.';

  const getBookingDurationDays = (): number => {
    if (!startDate || !bookingEndDate) return 0;
    if (durationType === 'full-day') {
      return Math.ceil((bookingEndDate.getTime() - startDate.getTime()) / 86400000) + 1;
    }
    if (durationType === 'half-day' || durationType === 'half-day-am' || durationType === 'half-day-pm') {
      return Math.ceil((bookingEndDate.getTime() - startDate.getTime()) / 86400000) + 1;
    }
    if (durationType === 'hourly' && startTime && endTime) {
      const [sh, sm] = startTime.split(':').map(Number);
      const [eh, em] = endTime.split(':').map(Number);
      const s = new Date(startDate); s.setHours(sh, sm, 0, 0);
      const e = new Date(bookingEndDate); e.setHours(eh, em, 0, 0);
      return (e.getTime() - s.getTime()) / 86400000;
    }
    return 0;
  };

  const checkWeeklyOverlap = (): boolean => {
    if (selectedDays.length <= 1) return false;
    const durationDays = getBookingDurationDays();
    if (durationDays <= 1) return false;

    const dayOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    const sortedIndices = selectedDays
      .map(d => dayOrder.indexOf(d))
      .filter(i => i >= 0)
      .sort((a, b) => a - b);

    for (let i = 0; i < sortedIndices.length; i++) {
      const current = sortedIndices[i];
      const next = sortedIndices[(i + 1) % sortedIndices.length];
      const gap = next > current ? next - current : 7 - current + next;
      if (durationDays > gap) return true;
    }
    return false;
  };

  // ── Save / Cancel / Remove ───────────────────────────────────────────────────
  const handleSave = () => {
    if (!endDate) {
      setValidationErrors({ endDate: 'Please select an end date' });
      return;
    }

    // Daily validation: multi-day bookings cannot recur daily
    if (repeatUnit === 'day' && isMultiDayBooking()) {
      setDurationOverlapAlert(true);
      return;
    }

    // Weekly validation: check if booking duration overlaps with next selected day
    if (repeatUnit === 'week' && checkWeeklyOverlap()) {
      setDurationOverlapAlert(true);
      return;
    }

    onRecurrencePatternChange(
      repeatUnit === 'day' ? 'daily' : repeatUnit === 'week' ? 'weekly' : 'monthly'
    );
    onCustomIntervalChange(repeatInterval);
    onRecurrenceEndTypeChange('on-date');
    onRecurrenceEndDateChange(endDate);
    onSeriesStartDateChange?.(seriesStartDate);

    // Pass back days of week for weekly recurrence
    if (repeatUnit === 'week' && onDaysOfWeekChange) {
      onDaysOfWeekChange(selectedDays.length > 0 ? selectedDays : (seriesStartDate ? [['sunday','monday','tuesday','wednesday','thursday','friday','saturday'][seriesStartDate.getDay()]] : []));
    }

    // Pass back monthly recurrence details
    if (repeatUnit === 'month') {
      if (monthlyType === 'day-of-month') {
        onMonthlyRecurrenceTypeChange?.('day-of-month');
        onDayOfMonthChange?.(seriesStartDate ? getDate(seriesStartDate) : undefined);
        onRelativeWeekdayChange?.(undefined);
      } else {
        onMonthlyRecurrenceTypeChange?.('relative-weekday');
        onDayOfMonthChange?.(undefined);
        if (seriesStartDate) {
          onRelativeWeekdayChange?.({
            occurrence: getOrdinalLabel(seriesStartDate) as 'first' | 'second' | 'third' | 'fourth' | 'last',
            weekday: getWeekdayName(seriesStartDate),
          });
        }
      }
    }

    onOpenChange(false);
  };

  const handleDiscard = () => {
    setValidationErrors({});
    onOpenChange(false);
  };

  const handleRemove = () => {
    onRecurrencePatternChange('none');
    onRecurrenceEndTypeChange('never');
    onRecurrenceEndDateChange(undefined);
    onCustomIntervalChange('1');
    onDaysOfWeekChange?.([]);
    onDayOfMonthChange?.(undefined);
    onMonthlyRecurrenceTypeChange?.('day-of-month');
    onRelativeWeekdayChange?.(undefined);
    onOpenChange(false);
  };

  // ── Weekday name from seriesStartDate ────────────────────────────────────────
  const weekdayOfStart = seriesStartDate ? getWeekdayName(seriesStartDate) : 'day';

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay />
        <DialogPrimitive.Content
          className={cn(
            'fixed z-50 w-full max-w-[400px] border bg-background shadow-lg duration-200',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
            'sm:rounded-xl [&>button]:hidden',
            !containerPosition && 'left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%]'
          )}
          style={getPositionStyles()}
        >
          {/* ── Header ── */}
          <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b">
            <div className="flex items-center gap-2">
              <Repeat className="h-4 w-4 text-primary" />
              <div>
                <span className="text-sm font-semibold text-foreground">Set Repeat</span>
                {durationType === 'hourly' && startTime && endTime && (
                  <p className="text-[11px] text-muted-foreground leading-none mt-0.5">
                    {startTime} → {endTime}
                  </p>
                )}
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 rounded-full text-muted-foreground hover:text-foreground"
              onClick={() => onOpenChange(false)}
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>

          <div className="px-5 py-4 space-y-5">

            {/* ── Frequency segmented control ── */}
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Repeats
              </Label>
              <div className="grid grid-cols-3 gap-1.5 p-1 bg-muted rounded-lg">
                {(['day', 'week', 'month'] as RepeatUnit[]).map((unit) => {
                  const label = unit === 'day' ? 'Daily' : unit === 'week' ? 'Weekly' : 'Monthly';
                  const disabled = unit === 'day' && isDailyDisabled;
                  return (
                    <button
                      key={unit}
                      type="button"
                      disabled={disabled}
                      onClick={() => setRepeatUnit(unit)}
                      className={cn(
                        'py-1.5 rounded-md text-sm font-medium transition-all',
                        repeatUnit === unit
                          ? 'bg-background text-foreground shadow-sm'
                          : 'text-muted-foreground hover:text-foreground',
                        disabled && 'opacity-40 cursor-not-allowed'
                      )}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* ── Day chips (weekly only) ── */}
            {repeatUnit === 'week' && (
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  On
                </Label>
                <div className="flex gap-1.5">
                  {DAYS_OF_WEEK.map((day) => (
                    <button
                      key={day.value}
                      type="button"
                      title={day.fullName}
                      onClick={() => toggleDay(day.value)}
                      className={cn(
                        'flex-1 h-8 rounded-md text-xs font-semibold transition-all border',
                        selectedDays.includes(day.value)
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'bg-background text-muted-foreground border-border hover:border-primary/50 hover:text-foreground'
                      )}
                    >
                      {day.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* ── Monthly options ── */}
            {repeatUnit === 'month' && (
              <div className="space-y-2">
                <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  On
                </Label>
                <div className="space-y-1.5">
                  {/* Option 1 — Day of month (e.g. "Monthly on day 15") */}
                  <button
                    type="button"
                    onClick={() => setMonthlyType('day-of-month')}
                    className={cn(
                      'w-full px-3 py-2.5 rounded-lg text-sm border transition-all text-left',
                      monthlyType === 'day-of-month'
                        ? 'border-primary bg-primary/5 text-primary font-medium'
                        : 'border-border text-foreground hover:border-primary/40'
                    )}
                  >
                    Monthly on day {seriesStartDate ? getDate(seriesStartDate) : '—'}
                  </button>

                  {/* Option 2 — Relative weekday (e.g. "Monthly on the third Tuesday") */}
                  <button
                    type="button"
                    onClick={() => setMonthlyType('weekday-of-month')}
                    className={cn(
                      'w-full px-3 py-2.5 rounded-lg text-sm border transition-all text-left',
                      monthlyType === 'weekday-of-month'
                        ? 'border-primary bg-primary/5 text-primary font-medium'
                        : 'border-border text-foreground hover:border-primary/40'
                    )}
                  >
                    Monthly on the {seriesStartDate ? getOrdinalLabel(seriesStartDate) : 'first'} {weekdayOfStart}
                  </button>
                </div>
              </div>
            )}

            {/* ── Advanced: custom interval ── */}
            {repeatUnit !== 'day' && (
              <div>
                <button
                  type="button"
                  onClick={() => setShowAdvanced(p => !p)}
                  className="text-xs text-primary hover:underline"
                >
                  {showAdvanced ? 'Hide advanced options' : 'Advanced: set custom interval'}
                </button>
                {showAdvanced && (
                  <div className="mt-2 flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Every</span>
                    <Input
                      type="number"
                      min="1"
                      value={repeatInterval}
                      onChange={(e) => setRepeatInterval(e.target.value)}
                      className="w-16 h-8 text-sm text-center"
                    />
                    <span className="text-sm text-muted-foreground">
                      {repeatUnit === 'week' ? 'week(s)' : 'month(s)'}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* ── Start date + End date in a two-column row ── */}
            <div className="grid grid-cols-2 gap-3">

              {/* Starts on */}
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Starts on
                </Label>
                <Popover open={seriesStartOpen} onOpenChange={setSeriesStartOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        'w-full justify-start text-left font-normal h-9 text-sm',
                        !seriesStartDate && 'text-muted-foreground'
                      )}
                    >
                      <CalendarIcon className="mr-2 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                      {seriesStartDate ? format(seriesStartDate, 'dd MMM yyyy') : 'Select'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={seriesStartDate}
                      onSelect={(date) => {
                        setSeriesStartDate(date);
                        setSeriesStartOpen(false);
                      }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Ends on */}
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Ends on <span className="text-destructive normal-case tracking-normal">*</span>
                </Label>
                <Popover open={endDateOpen} onOpenChange={setEndDateOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        'w-full justify-start text-left font-normal h-9 text-sm',
                        !endDate && 'text-muted-foreground',
                        validationErrors.endDate && 'border-destructive'
                      )}
                    >
                      <CalendarIcon className="mr-2 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                      {endDate ? format(endDate, 'dd MMM yyyy') : 'Select'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={endDate}
                      onSelect={(date) => {
                        setEndDate(date);
                        setEndDateOpen(false);
                        if (date) setValidationErrors({});
                      }}
                      disabled={(date) => seriesStartDate ? date < seriesStartDate : false}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                {validationErrors.endDate && (
                  <p className="text-xs text-destructive">{validationErrors.endDate}</p>
                )}
              </div>
            </div>

            {/* ── Live preview pill ── */}
            <div className="flex items-center gap-2 px-3 py-2 rounded-full bg-primary/8 border border-primary/20">
              <Repeat className="h-3 w-3 text-primary shrink-0" />
              <span className="text-xs text-primary font-medium truncate">
                {getPreviewText()}
              </span>
            </div>

          </div>

          {/* ── Footer ── */}
          <div className="flex items-center gap-2 px-5 py-3 border-t bg-muted/30">
            <Button
              type="button"
              onClick={handleSave}
              className="flex-1 h-8 text-sm"
            >
              Save
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={handleDiscard}
              className="flex-1 h-8 text-sm"
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={handleRemove}
              className="h-8 w-8 p-0 text-destructive border-destructive/30 hover:bg-destructive/10 hover:border-destructive"
              title="Remove repeat"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>

        </DialogPrimitive.Content>
      </DialogPortal>

      {/* Duration overlap validation alert */}
      <AlertDialog open={durationOverlapAlert} onOpenChange={setDurationOverlapAlert}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Recurrence Conflict</AlertDialogTitle>
            <AlertDialogDescription>
              {DURATION_OVERLAP_MSG}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setDurationOverlapAlert(false)}>
              Ok
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
